import type { IconName } from 'shared/ui/icons/Icon';

/** Nodo del menú que devuelve el módulo de seguridad y se guarda en sessionStorage. */
export interface MenuNode {
  menuId: number;
  name: string;
  transaction?: { route?: string | null } | null;
  Nodes?: MenuNode[];
}

export interface NavItem {
  id: string;
  label: string;
  path: string;
  icon: IconName;
}

export interface NavGroup {
  id: string;
  label: string;
  icon: IconName;
  items: NavItem[];
}

/** Todas las pantallas con sesión cuelgan de `/src` (ver `routes/app.jsx`). */
export const BASE_PATH = '/src';
export const HOME_PATH = `${BASE_PATH}/home`;

/**
 * Las rutas del menú vienen del backend tal cual se registraron: algunas con
 * espacios al final (`/send_payment `) o en mayúsculas (`/COUNTRY`). Se comparan
 * sin espacios, sin barras finales y sin distinguir mayúsculas, igual que hace
 * React Router al resolverlas.
 */
const normalizar = (ruta: string): string => {
  let fin = ruta.length;
  while (fin > 0 && ruta[fin - 1] === '/') {
    fin--;
  }
  return ruta.slice(0, fin).toLowerCase();
};

const rutaDeMenu = (route: string): string => `${BASE_PATH}/${route.trim().replace(/^\/+/, '')}`;

/** El módulo de seguridad no envía iconos: cada pantalla tiene el suyo según su ruta. */
const ROUTE_ICONS: Record<string, IconName> = {
  '/country': 'globe',
  '/type': 'tag',
  '/message': 'fileText',
  '/connectionsftp': 'plug',
  '/template': 'columns',
  '/assign_template': 'list',
  '/branch': 'building',
  '/account': 'idCard',
  '/limitamountentity': 'lock',
  '/channelentitypayment': 'key',
  '/collect_entity': 'building',
  '/channel_transaction': 'refresh',
  '/channel': 'server',
  '/send_payment': 'upload',
  '/monitor': 'dashboard',
  '/conciliation_monitor': 'activity',
  '/clientsap': 'users',
  '/conciliation': 'checkCircle',
  '/history': 'clock',
};

export const iconForRoute = (route: string): IconName =>
  ROUTE_ICONS[normalizar(`/${route.trim().replace(/^\/+/, '')}`)] || 'file';

export const iconForGroup = (name: string): IconName => {
  const key = name.toLowerCase();
  if (key.includes('config')) return 'settings';
  if (key.includes('recauda')) return 'chart';
  if (key.includes('entidad')) return 'building';
  if (key.includes('canal')) return 'server';
  return 'folder';
};

export const isUnder = (pathname: string, path: string): boolean => {
  const actual = normalizar(pathname);
  const destino = normalizar(path);
  return actual === destino || actual.startsWith(`${destino}/`);
};

/** Lee el menú guardado al iniciar sesión; si no hay o está dañado, uno vacío. */
export const readSessionMenu = (): MenuNode[] => {
  try {
    const menu = JSON.parse(sessionStorage.getItem('menu') || '[]');
    return Array.isArray(menu) ? menu : [];
  } catch {
    return [];
  }
};

/** Grupos del sidebar: los del menú del módulo de seguridad, en su orden. */
export function buildNavigation(menu: MenuNode[]): NavGroup[] {
  return menu.map((node) => ({
    id: `mds-${node.menuId}`,
    label: node.name,
    icon: iconForGroup(node.name),
    items: (node.Nodes ?? [])
      .filter((leaf) => !!leaf.transaction?.route?.trim())
      .map((leaf) => {
        const route = leaf.transaction?.route as string;
        return {
          id: `mds-${leaf.menuId}`,
          label: leaf.name,
          path: rutaDeMenu(route),
          icon: iconForRoute(route),
        };
      }),
  }));
}

export const groupOfPath = (pathname: string, groups: NavGroup[]): NavGroup | undefined =>
  groups.find((group) => group.items.some((item) => isUnder(pathname, item.path)));
